import React, { lazy, Suspense, useState } from 'react';
import {
  Navigate, Route, Routes, useLocation, useNavigate,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LinearProgress } from '@mui/material';
import { handleErrorResponse } from './common/util/utils';
import MainPage from './main/MainPage';
import LoginPage from './login/LoginPage';
import useQuery from './common/util/useQuery';
import { useEffectAsync } from './reactHelper';
import { devicesActions } from './store';
import App from './App';

const ServerPage = lazy(() => import('./settings/ServerPage'));
const UsersPage = lazy(() => import('./settings/UsersPage'));
const RecoveriesPage = lazy(() => import('./settings/RecoveriesPage'));
const DevicePage = lazy(() => import('./settings/DevicePage'));
const UserPage = lazy(() => import('./settings/UserPage'));
const RecoveryPage = lazy(() => import('./settings/RecoveryPage'));
const NotificationsPage = lazy(() => import('./settings/NotificationsPage'));
const NotificationPage = lazy(() => import('./settings/NotificationPage'));
const GroupsPage = lazy(() => import('./settings/GroupsPage'));
const GroupPage = lazy(() => import('./settings/GroupPage'));
const PositionPage = lazy(() => import('./other/PositionPage'));
const NetworkPage = lazy(() => import('./other/NetworkPage'));
const EventReportPage = lazy(() => import('./reports/EventReportPage'));
const ReplayPage = lazy(() => import('./other/ReplayPage'));
const TripReportPage = lazy(() => import('./reports/TripReportPage'));
const TopStopReportPage = lazy(() => import('./reports/TopStopReportPage'));
const StopReportPage = lazy(() => import('./reports/StopReportPage'));
const ChartReportPage = lazy(() => import('./reports/ChartReportPage'));
const MaintenancesPage = lazy(() => import('./settings/MaintenancesPage'));
const MaintenancePage = lazy(() => import('./settings/MaintenancePage'));
const InstallExclusionsPage = lazy(() => import('./settings/InstallExclusionsPage'));
const InstallExclusionPage = lazy(() => import('./settings/InstallExclusionPage'));
const CommandsPage = lazy(() => import('./settings/CommandsPage'));
const CommandPage = lazy(() => import('./settings/CommandPage'));
const GroupTypeCommandsPage = lazy(() => import('./settings/GroupTypeCommandsPage'));
const StatisticsPage = lazy(() => import('./reports/StatisticsPage'));
const ResetPasswordPage = lazy(() => import('./login/ResetPasswordPage'));
const InstallPage = lazy(() => import('./install/InstallPage'));
const TermsAndConditionsPage = lazy(() => import('./login/TermsAndConditionsPage'));
const GeofencesPage = lazy(() => import('./other/GeofencesPage'));
const GeofencePage = lazy(() => import('./settings/GeofencePage'));
const EventPage = lazy(() => import('./other/EventPage'));
const BulkAddPage = lazy(() => import('./settings/BulkAddPage'));
const BulkTransferPage = lazy(() => import('./settings/BulkTransferPage'));
const BroadcastPage = lazy(() => import('./settings/BroadcastPage'));
const PreferencesPage = lazy(() => import('./settings/PreferencesPage'));
const AccumulatorsPage = lazy(() => import('./settings/AccumulatorsPage'));
const CommandSendPage = lazy(() => import('./settings/CommandSendPage'));
const DevicesPage = lazy(() => import('./settings/DevicesPage'));
const FindRecoveryPage = lazy(() => import('./tools/FindRecoveryPage'));
const FindTowingPage = lazy(() => import('./tools/FindTowingPage'));
const FindAuctionPage = lazy(() => import('./tools/FindAuctionPage'));
const FindLocksmithPage = lazy(() => import('./tools/FindLocksmithPage'));
const FindGPSInstallerPage = lazy(() => import('./tools/FindGPSInstallerPage'));
const AddressStopsPage = lazy(() => import('./reports/AddressStopsPage'));
const AddressVehiclesPage = lazy(() => import('./reports/AddressVehiclesPage'));
const RecoveryVehiclesPage = lazy(() => import('./reports/RecoveryVehiclesPage'));

const Navigation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [redirectsHandled, setRedirectsHandled] = useState(false);

  const { pathname } = useLocation();
  const query = useQuery();

  useEffectAsync(async () => {
    if (query.get('filter')) {
      dispatch(devicesActions.filter(query.get('filter')));
    }
    if (query.get('token')) {
      const token = query.get('token');
      await fetch(`/api/session?token=${encodeURIComponent(token)}`);
      navigate(pathname);
    } else if (query.get('deviceId')) {
      const deviceId = query.get('deviceId');
      const response = await fetch(`/api/devices?uniqueId=${deviceId}`);
      if (response.ok) {
        const items = await response.json();
        if (items.length > 0) {
          dispatch(devicesActions.select(items[0].id));
        }
      } else {
        await handleErrorResponse(response);
      }
      navigate('/');
    } else if (query.get('eventId')) {
      const eventId = parseInt(query.get('eventId'), 10);
      navigate(`/event/${eventId}`);
    } else {
      setRedirectsHandled(true);
    }
  }, [query]);

  if (!redirectsHandled) {
    return (<LinearProgress />);
  }
  return (
    <Suspense fallback={<LinearProgress />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/install" element={<InstallPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        <Route path="/" element={<App />}>
          <Route index element={<MainPage />} />

          <Route path="position/:id" element={<PositionPage />} />
          <Route path="network/:positionId" element={<NetworkPage />} />
          <Route path="event/:id" element={<EventPage />} />
          <Route path="replay" element={<ReplayPage />} />
          <Route path="geofences" element={<GeofencesPage />} />

          <Route path="settings">
            <Route path="accumulators/:deviceId" element={<AccumulatorsPage />} />
            <Route path="commands" element={<CommandsPage />} />
            <Route path="command/:id" element={<CommandPage />} />
            <Route path="command" element={<CommandPage />} />
            <Route path="command-send/:deviceId" element={<CommandSendPage />} />
            <Route path="group-type-commands" element={<GroupTypeCommandsPage />} />
            <Route path="devices" element={<DevicesPage />} />
            <Route path="device/:id" element={<DevicePage />} />
            <Route path="device" element={<DevicePage />} />
            <Route path="geofence/:id" element={<GeofencePage />} />
            <Route path="geofence" element={<GeofencePage />} />
            <Route path="groups" element={<GroupsPage />} />
            <Route path="group/:id" element={<GroupPage />} />
            <Route path="group" element={<GroupPage />} />
            <Route path="maintenances" element={<MaintenancesPage />} />
            <Route path="maintenance/:id" element={<MaintenancePage />} />
            <Route path="maintenance" element={<MaintenancePage />} />
            <Route path="installexclusions" element={<InstallExclusionsPage />} />
            <Route path="installexclusion/:id" element={<InstallExclusionPage />} />
            <Route path="installexclusion" element={<InstallExclusionPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="notification/:id" element={<NotificationPage />} />
            <Route path="notification" element={<NotificationPage />} />
            <Route path="bulkadd" element={<BulkAddPage />} />
            <Route path="bulktransfer" element={<BulkTransferPage />} />
            <Route path="broadcast" element={<BroadcastPage />} />
            <Route path="preferences" element={<PreferencesPage />} />
            <Route path="server" element={<ServerPage />} />
            <Route path="users" element={<UsersPage recovery={0} />} />
            <Route path="recoveries" element={<RecoveriesPage />} />
            <Route path="user/:id" element={<UserPage />} />
            <Route path="user" element={<UserPage />} />
            <Route path="recovery/:id" element={<RecoveryPage />} />
            <Route path="recovery" element={<RecoveryPage />} />
          </Route>

          <Route path="reports">
            <Route path="chart" element={<ChartReportPage />} />
            <Route path="event" element={<EventReportPage />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="topstop" element={<TopStopReportPage />} />
            <Route path="stop" element={<StopReportPage />} />
            <Route path="trip" element={<TripReportPage />} />
            <Route path="address-stops" element={<AddressStopsPage />} />
            <Route path="address-vehicles" element={<AddressVehiclesPage />} />
            <Route path="recovery-vehicles" element={<RecoveryVehiclesPage />} />
          </Route>

          <Route path="tools">
            <Route path="find-recovery" element={<FindRecoveryPage />} />
            <Route path="find-towing" element={<FindTowingPage />} />
            <Route path="find-auction" element={<FindAuctionPage />} />
            <Route path="find-locksmith" element={<FindLocksmithPage />} />
            <Route path="find-gpsinstaller" element={<FindGPSInstallerPage />} />
            <Route path="stops-at-address" element={<Navigate to="/reports/address-stops" replace />} />
            <Route path="vehicles-at-location" element={<Navigate to="/reports/address-vehicles" replace />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
};

export default Navigation;
